import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    TextField,
    Typography,
    MenuItem,
    Alert,
    IconButton,
    Paper,
    List,
    ListItem,
    ListItemButton,
    Stack,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import { IconTrash, IconPlus, IconUserPlus } from '@tabler/icons';
import { useHistory } from 'react-router-dom';
import debounce from 'lodash.debounce';
import api from '../../../../services/api';

const formatCurrencyBR = (value) => {
    const number = Number(value);
    if (Number.isNaN(number)) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(number);
};

const CreateSalesOrder = () => {
    const history = useHistory();

    const [appId, setAppId] = useState(null);
    const [status, setStatus] = useState({ type: '', message: '' });

    // organização (obrigatória)
    const [orgSearch, setOrgSearch] = useState('');
    const [orgResults, setOrgResults] = useState([]);
    const [showOrgResults, setShowOrgResults] = useState(false);
    const [organization, setOrganization] = useState(null);

    const [orgDialogOpen, setOrgDialogOpen] = useState(false);
    const [newOrgForm, setNewOrgForm] = useState({ name: '', cnpj: '', phone: '', email: '' });
    const [savingOrg, setSavingOrg] = useState(false);

    const [orderDate, setOrderDate] = useState(new Date().toISOString().slice(0, 10));
    const [notes, setNotes] = useState('');

    const [products, setProducts] = useState([]);
    const [services, setServices] = useState([]);
    const [items, setItems] = useState([]);

    const [warehouses, setWarehouses] = useState([]);
    const [warehouseId, setWarehouseId] = useState('');

    const [paymentTerms, setPaymentTerms] = useState([]);
    const [paymentTermId, setPaymentTermId] = useState('');
    const [discountPercent, setDiscountPercent] = useState('');
    const [discountAmount, setDiscountAmount] = useState('');

    // linha em edição
    const [itemType, setItemType] = useState('produto');
    const [selectedProductId, setSelectedProductId] = useState('');
    const [selectedVariacaoId, setSelectedVariacaoId] = useState('');
    const [selectedServiceId, setSelectedServiceId] = useState('');
    const [quantidade, setQuantidade] = useState(1);

    // 🔹 Buscar app padrão + carregar produtos
    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('v1/apps/default/');
                setAppId(res.data.app.id);
            } catch (error) {
                console.error(error);
                setStatus({ type: 'error', message: 'Erro ao buscar app padrão' });
            }
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        (async () => {
            try {
                const res = await api.get(`v1/apps/${appId}/products/`);
                setProducts(res.data || []);
            } catch (error) {
                console.error(error);
            }
        })();
    }, [appId]);

    useEffect(() => {
        if (!appId) return;
        (async () => {
            try {
                const res = await api.get(`v1/apps/${appId}/inventory/warehouses/`);
                setWarehouses(res.data || []);
            } catch (error) {
                console.error(error);
            }
        })();
    }, [appId]);

    useEffect(() => {
        if (!appId) return;
        (async () => {
            try {
                const res = await api.get(`v1/apps/${appId}/services/`);
                setServices(res.data.services || []);
            } catch (error) {
                console.error(error);
            }
        })();
    }, [appId]);

    useEffect(() => {
        if (!appId) return;
        (async () => {
            try {
                const res = await api.get(`v1/apps/${appId}/payment-terms/`);
                setPaymentTerms(res.data || []);
            } catch (error) {
                console.error(error);
            }
        })();
    }, [appId]);

    // 🔹 Busca de organização (autocomplete)
    const handleOrgSearch = useMemo(
        () =>
            debounce(async (value, currentAppId) => {
                if (!value || !currentAppId) {
                    setOrgResults([]);
                    setShowOrgResults(false);
                    return;
                }
                try {
                    const res = await api.get(`v1/apps/${currentAppId}/organizations/?search=${encodeURIComponent(value)}`);
                    setOrgResults(res.data.organizations || []);
                    setShowOrgResults(true);
                } catch (error) {
                    console.error(error);
                }
            }, 400),
        []
    );

    const handleOrgSearchChange = (e) => {
        const value = e.target.value;
        setOrgSearch(value);
        setOrganization(null);
        handleOrgSearch(value, appId);
    };

    const selectOrganization = async (org) => {
        setOrganization(org);
        setOrgSearch(org.name);
        setShowOrgResults(false);

        if (!appId) return;
        try {
            const res = await api.get(`v1/apps/${appId}/organizations/${org.id}/`);
            const data = res.data?.organization;
            if (data?.default_payment_term) {
                setPaymentTermId((prev) => prev || data.default_payment_term);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleOpenOrgDialog = () => {
        setNewOrgForm({ name: orgSearch, cnpj: '', phone: '', email: '' });
        setOrgDialogOpen(true);
    };

    const handleSaveNewOrg = async () => {
        if (!newOrgForm.name.trim()) {
            setStatus({ type: 'error', message: 'Informe o nome do cliente' });
            return;
        }
        setSavingOrg(true);
        try {
            const response = await api.post(`v1/apps/${appId}/organizations/`, {
                ...newOrgForm,
                is_customer: true,
                is_supplier: false
            });
            const newOrg = { id: response.data.organization_id, name: newOrgForm.name, cnpj: newOrgForm.cnpj };
            selectOrganization(newOrg);
            setOrgDialogOpen(false);
            setStatus({ type: '', message: '' });
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao criar cliente' });
        } finally {
            setSavingOrg(false);
        }
    };

    // 🔹 Itens do pedido
    const selectedProduct = products.find((p) => String(p.id) === String(selectedProductId));
    const variacoesDoProduto = selectedProduct?.variacoes || [];
    const selectedVariacao = variacoesDoProduto.find((v) => String(v.id) === String(selectedVariacaoId));
    const selectedService = services.find((s) => String(s.id) === String(selectedServiceId));

    const addItem = () => {
        const qtd = Number(quantidade);
        if (!qtd || qtd <= 0) {
            setStatus({ type: 'error', message: 'Quantidade deve ser maior que zero' });
            return;
        }

        if (itemType === 'produto') {
            if (!selectedVariacao) {
                setStatus({ type: 'error', message: 'Selecione um produto e uma variação' });
                return;
            }
            setItems((prev) => [
                ...prev,
                {
                    produto_variacao: selectedVariacao.id,
                    service: null,
                    nome: `${selectedProduct.nome} - ${selectedVariacao.nome_variacao}`,
                    quantidade: qtd,
                    valor_unitario: selectedVariacao.valor_venda
                }
            ]);
            setSelectedProductId('');
            setSelectedVariacaoId('');
        } else {
            if (!selectedService) {
                setStatus({ type: 'error', message: 'Selecione um serviço' });
                return;
            }
            setItems((prev) => [
                ...prev,
                {
                    produto_variacao: null,
                    service: selectedService.id,
                    nome: selectedService.name,
                    quantidade: qtd,
                    valor_unitario: selectedService.price
                }
            ]);
            setSelectedServiceId('');
        }

        setQuantidade(1);
        setStatus({ type: '', message: '' });
    };

    const removeItem = (index) => {
        setItems((prev) => prev.filter((_, i) => i !== index));
    };

    const subtotal = items.reduce((acc, item) => acc + Number(item.quantidade) * Number(item.valor_unitario), 0);
    const total = Math.max(subtotal - subtotal * (Number(discountPercent) || 0) / 100 - (Number(discountAmount) || 0), 0);

    // 🔹 Submit
    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus({ type: '', message: '' });

        if (!appId) {
            setStatus({ type: 'error', message: 'App não carregado ainda' });
            return;
        }
        if (!organization) {
            setStatus({ type: 'error', message: 'Selecione uma organização (cliente)' });
            return;
        }
        if (items.length === 0) {
            setStatus({ type: 'error', message: 'Adicione ao menos um item ao pedido' });
            return;
        }

        try {
            const payload = {
                organization: organization.id,
                warehouse: warehouseId || undefined,
                order_date: orderDate,
                notes,
                payment_term: paymentTermId || undefined,
                discount_percent: discountPercent || undefined,
                discount_amount: discountAmount || undefined,
                items: items.map((item) => ({
                    produto_variacao: item.produto_variacao || undefined,
                    service: item.service || undefined,
                    quantidade: item.quantidade,
                    valor_unitario: item.valor_unitario
                }))
            };
            const response = await api.post(`v1/apps/${appId}/sales_orders/`, payload);
            history.push(`/dashboard/sales/${response.data.id}/`);
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao criar pedido de venda' });
        }
    };

    return (
        <Box>
            <Typography variant="h3" gutterBottom sx={{ mb: 2 }}>
                Novo Pedido de Venda
            </Typography>

            <Card>
                <CardContent>
                    {status.message && (
                        <Alert severity={status.type} sx={{ mb: 2 }}>
                            {status.message}
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2}>
                            {/* Organização */}
                            <Grid item xs={12} sm={6} sx={{ position: 'relative' }}>
                                <Stack direction="row" spacing={1} alignItems="flex-start">
                                    <TextField
                                        label="Organização (cliente)"
                                        fullWidth
                                        required
                                        value={orgSearch}
                                        onChange={handleOrgSearchChange}
                                        onFocus={() => orgResults.length > 0 && setShowOrgResults(true)}
                                        placeholder="Buscar por nome ou CNPJ..."
                                    />
                                    <IconButton color="primary" title="Cadastrar novo cliente" onClick={handleOpenOrgDialog} sx={{ mt: 1 }}>
                                        <IconUserPlus size={20} />
                                    </IconButton>
                                </Stack>
                                {showOrgResults && orgResults.length > 0 && (
                                    <Paper sx={{ position: 'absolute', zIndex: 10, width: '100%', maxHeight: 240, overflowY: 'auto' }}>
                                        <List dense>
                                            {orgResults.map((org) => (
                                                <ListItem key={org.id} disablePadding>
                                                    <ListItemButton onClick={() => selectOrganization(org)}>
                                                        {org.name} {org.cnpj ? `- ${org.cnpj}` : ''}
                                                    </ListItemButton>
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Paper>
                                )}
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Data do pedido"
                                    type="date"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    value={orderDate}
                                    onChange={(e) => setOrderDate(e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    label="Depósito (opcional)"
                                    fullWidth
                                    helperText="Se não escolher, usa o depósito principal da conta"
                                    value={warehouseId}
                                    onChange={(e) => setWarehouseId(e.target.value)}
                                >
                                    <MenuItem value="">Depósito principal</MenuItem>
                                    {warehouses.map((w) => (
                                        <MenuItem key={w.id} value={w.id}>
                                            {w.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField
                                    select
                                    label="Condição de pagamento (opcional)"
                                    fullWidth
                                    value={paymentTermId}
                                    onChange={(e) => setPaymentTermId(e.target.value)}
                                >
                                    <MenuItem value="">Nenhuma</MenuItem>
                                    {paymentTerms.map((t) => (
                                        <MenuItem key={t.id} value={t.id}>
                                            {t.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={6} sm={4}>
                                <TextField
                                    label="Desconto (%)"
                                    type="number"
                                    fullWidth
                                    inputProps={{ min: 0, max: 100, step: '0.01' }}
                                    value={discountPercent}
                                    onChange={(e) => setDiscountPercent(e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={6} sm={4}>
                                <TextField
                                    label="Desconto (R$)"
                                    type="number"
                                    fullWidth
                                    inputProps={{ min: 0, step: '0.01' }}
                                    value={discountAmount}
                                    onChange={(e) => setDiscountAmount(e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    label="Observações"
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                />
                            </Grid>

                            {/* Itens */}
                            <Grid item xs={12}>
                                <Typography variant="h5" sx={{ mt: 2, mb: 1 }}>
                                    Itens
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <ToggleButtonGroup
                                    size="small"
                                    exclusive
                                    value={itemType}
                                    onChange={(e, value) => {
                                        if (!value) return;
                                        setItemType(value);
                                        setSelectedProductId('');
                                        setSelectedVariacaoId('');
                                        setSelectedServiceId('');
                                    }}
                                >
                                    <ToggleButton value="produto">Produto</ToggleButton>
                                    <ToggleButton value="servico">Serviço</ToggleButton>
                                </ToggleButtonGroup>
                            </Grid>

                            {itemType === 'produto' ? (
                                <>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            select
                                            label="Produto"
                                            fullWidth
                                            value={selectedProductId}
                                            onChange={(e) => {
                                                setSelectedProductId(e.target.value);
                                                setSelectedVariacaoId('');
                                            }}
                                        >
                                            {products.map((p) => (
                                                <MenuItem key={p.id} value={p.id}>
                                                    {p.nome}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            select
                                            label="Variação"
                                            fullWidth
                                            disabled={!selectedProductId}
                                            value={selectedVariacaoId}
                                            onChange={(e) => setSelectedVariacaoId(e.target.value)}
                                        >
                                            {variacoesDoProduto.map((v) => (
                                                <MenuItem key={v.id} value={v.id}>
                                                    {v.nome_variacao} - {formatCurrencyBR(v.valor_venda)} (estoque: {v.estoque})
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                </>
                            ) : (
                                <Grid item xs={12} sm={8}>
                                    <TextField
                                        select
                                        label="Serviço"
                                        fullWidth
                                        value={selectedServiceId}
                                        onChange={(e) => setSelectedServiceId(e.target.value)}
                                    >
                                        {services.map((s) => (
                                            <MenuItem key={s.id} value={s.id}>
                                                {s.name} - {formatCurrencyBR(s.price)}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                            )}

                            <Grid item xs={8} sm={2}>
                                <TextField
                                    label="Qtd"
                                    type="number"
                                    fullWidth
                                    inputProps={{ min: 1 }}
                                    value={quantidade}
                                    onChange={(e) => setQuantidade(e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={4} sm={2} display="flex" alignItems="center">
                                <Button variant="outlined" startIcon={<IconPlus size={18} />} onClick={addItem} fullWidth>
                                    Adicionar
                                </Button>
                            </Grid>

                            <Grid item xs={12}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Item</TableCell>
                                            <TableCell align="right">Quantidade</TableCell>
                                            <TableCell align="right">Valor unitário</TableCell>
                                            <TableCell align="right">Subtotal</TableCell>
                                            <TableCell align="right" />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {items.map((item, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{item.nome}</TableCell>
                                                <TableCell align="right">{item.quantidade}</TableCell>
                                                <TableCell align="right">{formatCurrencyBR(item.valor_unitario)}</TableCell>
                                                <TableCell align="right">
                                                    {formatCurrencyBR(item.quantidade * item.valor_unitario)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <IconButton size="small" onClick={() => removeItem(index)}>
                                                        <IconTrash size={16} />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {items.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center">
                                                    Nenhum item adicionado
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </Grid>

                            <Grid item xs={12} textAlign="right">
                                <Typography variant="h5">Total: {formatCurrencyBR(total)}</Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Box display="flex" justifyContent="flex-end" gap={2}>
                                    <Button onClick={() => history.push('/dashboard/sales')}>Voltar</Button>
                                    <Button type="submit" variant="contained">
                                        Salvar Pedido (Rascunho)
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </form>
                </CardContent>
            </Card>

            <Dialog open={orgDialogOpen} onClose={() => setOrgDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Novo cliente</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Nome"
                            fullWidth
                            required
                            value={newOrgForm.name}
                            onChange={(e) => setNewOrgForm({ ...newOrgForm, name: e.target.value })}
                        />
                        <TextField
                            label="CNPJ (opcional)"
                            fullWidth
                            value={newOrgForm.cnpj}
                            onChange={(e) => setNewOrgForm({ ...newOrgForm, cnpj: e.target.value })}
                        />
                        <TextField
                            label="Telefone (opcional)"
                            fullWidth
                            value={newOrgForm.phone}
                            onChange={(e) => setNewOrgForm({ ...newOrgForm, phone: e.target.value })}
                        />
                        <TextField
                            label="Email (opcional)"
                            fullWidth
                            value={newOrgForm.email}
                            onChange={(e) => setNewOrgForm({ ...newOrgForm, email: e.target.value })}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOrgDialogOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleSaveNewOrg} disabled={savingOrg}>
                        {savingOrg ? 'Salvando...' : 'Salvar e selecionar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CreateSalesOrder;
